import React from 'react';
import styled from 'styled-components';
import { FormTitle } from '../customComponents/FormTitle';
import { useLang } from '../context/LangContext';
import { formatPriceComponentsByLanguage } from '../utils/currency';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 24px;
`;

const Content = styled.div`
  padding: 16px 0;
`;

// 입찰 내역 리스트 컨테이너
const BidList = styled.div`
  display: flex;
  flex-direction: column;
`;

// 입찰 항목
const BidItem = styled.div<{ $isMyBid: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 16px;
  border-bottom: 1px solid #e0e0e0;
  
  ${({ $isMyBid }) => $isMyBid && `
    background-color: #f8f5f0;
    margin-bottom: 1px;
    border-bottom: 1px solid #f3f3f3;
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

// 입찰 번호 (왼쪽)
const BidNumber = styled.div<{ $isMyBid: boolean }>`
  font-size: 14px;
  font-weight: 500;
  
  ${({ $isMyBid }) => $isMyBid ? `
    color: #7a6647;
  ` : `
    color: #949494;
  `}
`;

// 입찰 금액 컨테이너 (오른쪽)
const BidAmount = styled.div<{ $isHighest: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  
  ${({ $isHighest }) => $isHighest && `
    // 최고가일 때 추가 스타일이 필요하면 여기에 추가
  `}
`;

// 환종 스타일
const Currency = styled.span<{ $isMyBid: boolean; $isHighest: boolean }>`
  font-size: 12px;
  font-weight: 500;
  
  ${({ $isMyBid, $isHighest }) => {
    return $isMyBid ? `color: #7a6647;` : `color: #949494;`;
  }}
`;

// 금액 스타일
const Amount = styled.span<{ $isMyBid: boolean; $isHighest: boolean }>`
  font-size: 16px;
  font-weight: 600;
  
  ${({ $isMyBid, $isHighest }) => {
    if ($isHighest) {
      return `
        color: #69b5b0;
        font-weight: 700;
      `;
    }
    return $isMyBid ? `color: #7a6647;` : `color: #666;`;
  }}
`;

// 데이터 없음 컨테이너
const NoDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

// 데이터 없음 이미지
const NoDataImage = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  opacity: 0.6;
`;

// 데이터 없음 텍스트
const NoDataText = styled.div`
  font-size: 14px;
  color: #999;
  font-weight: 500;
`;

interface BidHistoryProps {
  title?: string;
  bidHistory?: BidHistoryItem[];
  exchangeRate?: number; // 환율 추가
}

interface BidHistoryItem {
  bidNumber: string;
  amount: number;
  isMyBid: boolean;
}

// 다국어 텍스트
const bidHistoryTexts = {
  ko: {
    title: '입찰 내역',
    noData: '입찰 내역이 없습니다'
  },
  en: {
    title: 'Bid History',
    noData: 'No bid history available'
  }
};

// KRW 가격 포맷팅 함수 - 언어별 단일 통화로 변경
const formatPriceByLang = (price: number, exchangeRate: number, lang: 'ko' | 'en') => {
  const priceFormat = formatPriceComponentsByLanguage(price, exchangeRate, lang);
  return {
    currency: priceFormat.currency,
    amount: priceFormat.amount
  };
};

const BidHistory: React.FC<BidHistoryProps> = ({ 
  title,
  bidHistory,
  exchangeRate = 1300 // 기본 환율
}) => {
  const { lang } = useLang();
  const texts = bidHistoryTexts[lang];
  
  // props로 받은 값이 없으면 기본 다국어 텍스트 사용
  const sectionTitle = title || texts.title;
  // bidHistory가 빈 배열이거나 null/undefined인 경우 데이터 없음 처리
  const historyData = bidHistory && bidHistory.length > 0 ? bidHistory : null;

  // 가장 높은 금액 찾기 (데이터가 있을 때만)
  const highestAmount = historyData && historyData.length > 0 
    ? Math.max(...historyData.map((bid: BidHistoryItem) => bid.amount))
    : 0;

  return (
    <Container>
      <FormTitle>{sectionTitle}</FormTitle>
      <Content>
        {historyData && historyData.length > 0 ? (
          <BidList>
            {historyData.map((bid: BidHistoryItem, index: number) => {
              const priceFormat = formatPriceByLang(bid.amount, exchangeRate, lang);
              return (
                <BidItem key={index} $isMyBid={bid.isMyBid}>
                  <BidNumber $isMyBid={bid.isMyBid}>{bid.bidNumber}</BidNumber>
                  <BidAmount $isHighest={bid.amount === highestAmount}>
                    <Currency $isMyBid={bid.isMyBid} $isHighest={bid.amount === highestAmount}>
                      {priceFormat.currency}
                    </Currency>
                    <Amount $isMyBid={bid.isMyBid} $isHighest={bid.amount === highestAmount}>
                      {priceFormat.amount}
                    </Amount>
                  </BidAmount>
                </BidItem>
              );
            })}
          </BidList>
        ) : (
          <NoDataContainer>
            <NoDataImage src="/common/nodata.svg" alt="No data" />
            <NoDataText>{texts.noData}</NoDataText>
          </NoDataContainer>
        )}
      </Content>
    </Container>
  );
};

export default BidHistory;
